import { useEffect, useMemo, useRef, useState } from "react";
import Swal from "../../../../utils/apparifySwal";
import "@google/model-viewer";
import {
  Boxes,
  Camera,
  CheckCircle2,
  ImagePlus,
  Loader2,
  RefreshCcw,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { businessSwalBase } from "../../../utils/businessSwal";
import { businessProductsAPI } from "../../../services/businessProductsService";
import { getImageUrl } from "../../../../services/api";
import ShopSelect from "../../../../components/ShopSelect";
import {
  DEFAULT_VIEWER_CONFIG,
  ENVIRONMENT_PRESETS,
  mergeViewerConfig,
} from "../../../../utils/product3dViewer";
import { makeVariantKey } from "../utils/variantMatrix";

function viewerConfigsEqual(left = {}, right = {}) {
  return JSON.stringify(mergeViewerConfig(left)) === JSON.stringify(mergeViewerConfig(right));
}

function variantLabel(variant = {}) {
  const values = Object.values(variant.optionValues || {}).filter(Boolean);
  return values.length ? values.join(" / ") : variant.sku || "Variant";
}

function normalizeEntry(entry = {}, variant = {}) {
  const status = entry.status || null;
  const isArchived = String(status || "").toUpperCase() === "ARCHIVED";
  return {
    variantKey: makeVariantKey(variant.optionValues || {}),
    variantId: variant.id || null,
    label: variantLabel(variant),
    status,
    modelAssetId: isArchived ? null : (entry.modelAssetId || entry.originalAssetId || null),
    optimizedAssetId: entry.optimizedAssetId || null,
    posterAssetId: isArchived ? null : (entry.posterAssetId || null),
    originalUrl: entry.originalUrl || null,
    previewUrl: entry.previewUrl || null,
    posterUrl: isArchived ? null : (entry.posterUrl || null),
    viewerConfig: mergeViewerConfig(entry.viewerConfig || {}),
    processingReport: entry.processingReport || null,
    warnings: Array.isArray(entry.warnings) ? entry.warnings : [],
    archivedAt: entry.archivedAt || null,
    lastProcessedAt: entry.lastProcessedAt || null,
  };
}

function reconcileEntries(entries = [], variants = []) {
  const byKey = new Map(
    (entries || []).map((entry) => [String(entry.variantKey || "").trim(), entry])
  );

  return (variants || []).map((variant) => {
    const key = makeVariantKey(variant.optionValues || {});
    return normalizeEntry(byKey.get(key) || {}, variant);
  });
}

function formatNumber(value, digits = 4) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed.toFixed(digits).replace(/\.?0+$/, "");
}

function formatMegabytes(bytes) {
  const megabytes = Number(bytes) / (1024 * 1024);
  if (!Number.isFinite(megabytes)) return null;
  return `${Number(megabytes.toFixed(1))} MB`;
}

function formatUploadError(message) {
  const text = String(message || "Something went wrong.");
  const sizeMatch = text.match(/File size too large\.\s*Got\s+(\d+)\.\s*Maximum is\s+(\d+)\.?/i);
  if (!sizeMatch) return text;

  return `This file is ${formatMegabytes(sizeMatch[1])}. Maximum allowed size is ${formatMegabytes(sizeMatch[2])}.`;
}

function radiansToDegrees(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return (parsed * 180) / Math.PI;
}

function captureViewerConfigFromElement(viewer, baseConfig = DEFAULT_VIEWER_CONFIG) {
  const nextConfig = mergeViewerConfig(baseConfig);
  if (!viewer) return nextConfig;

  const orbit = viewer.getCameraOrbit?.();
  const target = viewer.getCameraTarget?.();
  const fieldOfView = viewer.getFieldOfView?.();

  if (orbit) {
    nextConfig.cameraOrbit = [
      `${formatNumber(radiansToDegrees(orbit.theta), 2) || "0"}deg`,
      `${formatNumber(radiansToDegrees(orbit.phi), 2) || "75"}deg`,
      `${formatNumber(orbit.radius, 3) || "1"}m`,
    ].join(" ");
  }

  if (target) {
    nextConfig.cameraTarget = [
      `${formatNumber(target.x, 3) || "0"}m`,
      `${formatNumber(target.y, 3) || "0"}m`,
      `${formatNumber(target.z, 3) || "0"}m`,
    ].join(" ");
  }

  const parsedFov = Number(fieldOfView);
  if (Number.isFinite(parsedFov)) {
    nextConfig.fieldOfView = `${formatNumber(parsedFov, 2) || "30"}deg`;
  }

  return nextConfig;
}

function captureCameraPoseFromElement(viewer) {
  const snapshot = captureViewerConfigFromElement(viewer, DEFAULT_VIEWER_CONFIG);
  return {
    cameraOrbit: snapshot.cameraOrbit,
    cameraTarget: snapshot.cameraTarget,
    fieldOfView: snapshot.fieldOfView,
  };
}

function statusChip(status) {
  const value = String(status || "EMPTY").toUpperCase();
  if (value === "READY") return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200";
  if (value === "PROCESSING") return "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-200";
  if (value === "FAILED") return "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-200";
  if (value === "ARCHIVED") return "border-zinc-200 bg-zinc-100 text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-300";
  return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200";
}

function statusLabel(status) {
  const value = String(status || "").toUpperCase();
  if (value === "READY") return "READY";
  if (value === "PROCESSING") return "PROCESSING";
  if (value === "FAILED") return "FAILED";
  if (value === "ARCHIVED") return "ARCHIVED";
  return "NO MODEL";
}

function formatKilobytes(bytes) {
  const parsed = Number(bytes);
  if (!Number.isFinite(parsed)) return null;
  return `${Math.round(parsed / 1024)} KB`;
}

function VariantRail({ entries, activeVariantKey, onSelectVariant }) {
  return (
    <aside className="rounded-2xl border border-zinc-200/75 bg-white p-3 dark:border-zinc-800/80 dark:bg-[#101114]">
      <div className="mb-2.5 flex items-center justify-between gap-3 px-1">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
          Variants
        </div>
        <span className="text-[11px] font-medium text-zinc-400 dark:text-zinc-500">
          {entries.length} variant{entries.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {entries.map((entry) => {
          const isActive = entry.variantKey === activeVariantKey;
          return (
            <button
              key={entry.variantKey}
              type="button"
              onClick={() => onSelectVariant(entry.variantKey)}
              className={
                "inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-colors duration-150 " +
                (isActive
                  ? "border-zinc-300 bg-zinc-100 text-zinc-900 ring-1 ring-zinc-300/60 dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-900"
                  : "border-zinc-200/80 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 dark:border-zinc-800 dark:bg-[#14161a] dark:text-zinc-200 dark:hover:border-zinc-700")
              }
            >
              <span className="text-sm font-medium leading-tight">{entry.label}</span>
              <span
                className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusChip(entry.status)}`}
              >
                {statusLabel(entry.status)}
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );
}

function ViewerToolbar({
  activeEntry,
  isBusy,
  modelInputRef,
  posterInputRef,
  onModelUpload,
  onPosterUpload,
  onRemoveModel,
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-xl bg-zinc-100/80 p-1.5 dark:bg-white/[0.04]">
      <button
        type="button"
        onClick={() => modelInputRef.current?.click()}
        disabled={isBusy}
        className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-zinc-900 px-3 text-xs font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
      >
        <UploadCloud size={14} />
        {activeEntry?.modelAssetId ? "Replace GLB" : "Upload GLB"}
      </button>
      <button
        type="button"
        onClick={() => posterInputRef.current?.click()}
        disabled={!activeEntry?.modelAssetId || isBusy}
        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white/90 px-3 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
      >
        <ImagePlus size={14} />
        Poster
      </button>
      <button
        type="button"
        onClick={onRemoveModel}
        disabled={!activeEntry?.modelAssetId || isBusy}
        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50/90 px-3 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200"
      >
        <Trash2 size={14} />
        Remove
      </button>
      <input
        ref={modelInputRef}
        type="file"
        accept=".glb,model/gltf-binary"
        className="hidden"
        onChange={onModelUpload}
      />
      <input
        ref={posterInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onPosterUpload}
      />
    </div>
  );
}

function ViewerStage({
  activeEntry,
  activePreset,
  hasReadyModel,
  modelReady,
  viewerError,
  viewerInstanceKey,
  viewerRef,
  appliedCameraConfig,
  viewerDraft,
  modelTransfer,
}) {
  return (
    <div
      className={`relative flex flex-1 flex-col overflow-hidden rounded-2xl border border-zinc-200/80 bg-gradient-to-br ${activePreset.stageClass} dark:border-zinc-800 dark:bg-[#17191d]`}
    >
      {/* Header strip */}
      <div className="flex items-center justify-between gap-3 border-b border-zinc-200/60 bg-white/60 px-4 py-2 backdrop-blur dark:border-zinc-800/60 dark:bg-black/20">
        <div className="flex items-center gap-2.5 text-xs">
          <span className="font-semibold text-zinc-700 dark:text-zinc-200">
            {activePreset.label}
          </span>
          <span className="text-zinc-400 dark:text-zinc-500">·</span>
          <span className="text-zinc-500 dark:text-zinc-400">{statusLabel(activeEntry?.status)}</span>
        </div>
        <span className="text-[11px] text-zinc-400 dark:text-zinc-500">Drag to orbit</span>
      </div>

      {/* Canvas area */}
      <div className="relative flex flex-1 p-3">
        <div className="relative min-h-[340px] w-full flex-1 overflow-hidden rounded-xl border border-white/30 bg-black/[0.02] dark:border-white/10 dark:bg-black/15 lg:min-h-[clamp(400px,58vh,620px)]">
          {modelTransfer && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/90 px-6 text-center backdrop-blur-sm dark:bg-[#0f1013]/92">
              <Loader2 className="h-8 w-8 animate-spin text-sky-600 dark:text-sky-400" />
              <div className="mt-4 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                {modelTransfer.action} 3D asset
              </div>
              <div className="mt-1 max-w-md truncate text-xs text-zinc-500 dark:text-zinc-400">
                {modelTransfer.fileName}
              </div>
              <div className="mt-4 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
                <div className="h-full w-2/3 animate-pulse rounded-full bg-sky-500 dark:bg-sky-400" />
              </div>
              <div className="mt-2 text-[11px] font-medium text-sky-700 dark:text-sky-300">
                {modelTransfer.phase === "saving"
                  ? "Upload complete. Linking the asset and starting optimization…"
                  : "Uploading securely. Keep this page open…"}
              </div>
            </div>
          )}

          {hasReadyModel && activeEntry?.previewUrl ? (
            <>
              <model-viewer
                key={`${activeEntry.variantKey}-${viewerInstanceKey}`}
                ref={viewerRef}
                src={activeEntry.previewUrl}
                poster={activeEntry.posterUrl ? getImageUrl(activeEntry.posterUrl) : undefined}
                className="h-full w-full bg-transparent"
                camera-controls
                touch-action="pan-y"
                camera-orbit={appliedCameraConfig.cameraOrbit}
                camera-target={appliedCameraConfig.cameraTarget}
                field-of-view={appliedCameraConfig.fieldOfView}
                min-camera-orbit={viewerDraft.minCameraOrbit}
                max-camera-orbit={viewerDraft.maxCameraOrbit}
                min-field-of-view={viewerDraft.minFieldOfView}
                max-field-of-view={viewerDraft.maxFieldOfView}
              />

              {!modelReady && !viewerError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/72 px-6 text-center dark:bg-black/36">
                  <Boxes className="text-zinc-400 dark:text-zinc-500" size={28} />
                  <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Loading 3D preview
                  </div>
                  <div className="max-w-sm text-xs text-zinc-500 dark:text-zinc-400">
                    Restoring the current camera and lighting state.
                  </div>
                </div>
              )}

              {viewerError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/82 px-6 text-center dark:bg-black/42">
                  <Boxes className="text-rose-400 dark:text-rose-300" size={28} />
                  <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Preview failed
                  </div>
                  <div className="max-w-sm text-xs text-zinc-500 dark:text-zinc-400">
                    {viewerError}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
              {activeEntry?.status === "PROCESSING" ? (
                <Loader2 className="animate-spin text-sky-500 dark:text-sky-400" size={28} />
              ) : (
                <Boxes className="text-zinc-400 dark:text-zinc-600" size={28} />
              )}
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                {activeEntry?.status === "PROCESSING"
                  ? "Model is processing"
                  : activeEntry?.status === "FAILED"
                    ? "Model optimization failed"
                    : "No ready 3D preview yet"}
              </div>
              <div className="max-w-sm text-xs text-zinc-500 dark:text-zinc-400">
                {activeEntry?.status === "PROCESSING"
                  ? "The uploaded GLB is being validated and optimized."
                  : activeEntry?.status === "FAILED"
                    ? activeEntry?.processingReport?.error ||
                      "Replace the model or retry with a valid GLB."
                    : "Upload a GLB and wait for optimization to finish."}
              </div>
              {activeEntry?.status === "PROCESSING" && (
                <div className="mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
                  <div className="h-full w-2/3 animate-pulse rounded-full bg-sky-500 dark:bg-sky-400" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ViewerSettingsCard({
  activePreset,
  viewerDraft,
  canInteractViewer,
  hasUnsavedChanges,
  usesSystemDefault,
  canSaveCurrentView,
  canRevertUnsaved,
  canSaveSystemDefault,
  activeEntry,
  isBusy,
  onEnvironmentChange,
  onExposureChange,
  onAutoRotateChange,
  onSaveCurrentView,
  onRevertToSavedView,
  onSaveSystemDefault,
  onRetryProcessing,
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-zinc-200/70 bg-white/90 p-3.5 dark:border-zinc-800/80 dark:bg-[#111317]">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">
          Viewer Settings
        </div>
        <span
          className={
            "inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold " +
            (hasUnsavedChanges
              ? "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200"
              : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200")
          }
        >
          {hasUnsavedChanges ? "Unsaved draft" : "Saved"}
        </span>
      </div>

      <div className="mt-2.5 flex flex-wrap gap-1.5 text-[10px]">
        <span className="inline-flex rounded-md border border-zinc-200 bg-zinc-50/90 px-2 py-0.5 font-medium text-zinc-600 dark:border-zinc-700/80 dark:bg-[#181a1e] dark:text-zinc-300">
          {activePreset.label}
        </span>
        <span className="inline-flex rounded-md border border-zinc-200 bg-zinc-50/90 px-2 py-0.5 font-medium text-zinc-600 dark:border-zinc-700/80 dark:bg-[#181a1e] dark:text-zinc-300">
          {usesSystemDefault ? "System default" : "Custom view"}
        </span>
      </div>

      <div className="mt-3 space-y-3 border-t border-zinc-200/70 pt-3 dark:border-zinc-800/80">
        <div className="block space-y-1">
          <div className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
            Environment
          </div>
          <ShopSelect
            value={viewerDraft.environmentPreset}
            onValueChange={onEnvironmentChange}
            options={Object.entries(ENVIRONMENT_PRESETS).map(([preset, meta]) => ({
              value: preset,
              label: meta.label,
            }))}
            disabled={!canInteractViewer}
            ariaLabel="Environment"
            align="start"
            minPanelWidth={220}
            triggerClassName="min-h-9 w-full rounded-xl px-3 py-2 text-sm"
            panelClassName="py-1"
            optionClassName="text-sm"
          />
        </div>

        <label className="block space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
              Exposure
            </span>
            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
              {viewerDraft.exposure}
            </span>
          </div>
          <input
            type="range"
            min="0.5"
            max="2.5"
            step="0.1"
            value={Number(viewerDraft.exposure || 1)}
            disabled={!canInteractViewer}
            onChange={onExposureChange}
            className="w-full disabled:cursor-not-allowed disabled:opacity-50"
          />
        </label>

        <label className="flex items-center justify-between gap-3 rounded-xl bg-zinc-50/80 px-3 py-2 dark:bg-[#181a1e]">
          <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
            Auto-rotate
          </span>
          <input
            type="checkbox"
            checked={Boolean(viewerDraft.autoRotate)}
            disabled={!canInteractViewer}
            onChange={onAutoRotateChange}
          />
        </label>
      </div>

      <div className="mt-3 space-y-2 border-t border-zinc-200/70 pt-3 dark:border-zinc-800/80">
        <button
          type="button"
          onClick={onSaveCurrentView}
          disabled={!canSaveCurrentView}
          className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg bg-zinc-900 px-3 text-xs font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
        >
          <Camera size={14} />
          Save Current View
        </button>
        <div className="grid min-w-0 grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={onRevertToSavedView}
            disabled={!canRevertUnsaved}
            className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-zinc-200 bg-white/90 px-2 text-[11px] font-medium text-zinc-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-[#191b20] dark:text-zinc-100"
          >
            <RefreshCcw size={12} />
            Revert
          </button>
          <button
            type="button"
            onClick={onSaveSystemDefault}
            disabled={!canSaveSystemDefault}
            className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-transparent bg-zinc-100/90 px-2 text-[11px] font-medium text-zinc-600 hover:bg-zinc-200/80 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/[0.04] dark:text-zinc-300 dark:hover:bg-white/[0.07]"
          >
            <RefreshCcw size={12} />
            Reset Default
          </button>
        </div>
        {activeEntry?.status === "FAILED" && activeEntry?.modelAssetId && (
          <button
            type="button"
            onClick={onRetryProcessing}
            disabled={isBusy}
            className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50/90 px-3 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200"
          >
            <RefreshCcw size={14} />
            Retry Processing
          </button>
        )}
      </div>
    </div>
  );
}

function PosterCard({
  activeEntry,
  variantImage,
  coverImage,
  isBusy,
  onUsePosterUrl,
  onClearPoster,
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-zinc-200/70 bg-white/90 p-3.5 dark:border-zinc-800/80 dark:bg-[#111317]">
      <div className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">
        Poster
      </div>

      {activeEntry?.posterUrl ? (
        <div className="mt-2 overflow-hidden rounded-xl border border-zinc-200/80 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900/70">
          <img
            src={getImageUrl(activeEntry.posterUrl)}
            alt=""
            className="aspect-video w-full object-cover"
          />
        </div>
      ) : (
        <div className="mt-2 rounded-xl border border-dashed border-zinc-300 bg-zinc-50/60 px-3 py-3.5 text-center text-xs text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-400">
          No poster assigned yet.
        </div>
      )}

      <div className="mt-2.5 grid min-w-0 gap-1.5">
        {variantImage && (
          <button
            type="button"
            onClick={() => onUsePosterUrl(variantImage)}
            disabled={!activeEntry?.modelAssetId || isBusy}
            className="inline-flex h-8 items-center justify-center rounded-lg border border-zinc-200 bg-white/90 px-3 text-[11px] font-medium text-zinc-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-[#191b20] dark:text-zinc-200"
          >
            Use Variant Image
          </button>
        )}
        {coverImage && (
          <button
            type="button"
            onClick={() => onUsePosterUrl(coverImage)}
            disabled={!activeEntry?.modelAssetId || isBusy}
            className="inline-flex h-8 items-center justify-center rounded-lg border border-zinc-200 bg-white/90 px-3 text-[11px] font-medium text-zinc-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-[#191b20] dark:text-zinc-200"
          >
            Use Cover Image
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={onClearPoster}
        disabled={!activeEntry?.posterUrl || !activeEntry?.modelAssetId || isBusy}
        className="mt-1.5 inline-flex h-8 w-full items-center justify-center rounded-lg border border-transparent bg-zinc-100/90 px-3 text-[11px] font-medium text-zinc-600 hover:bg-zinc-200/80 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/[0.04] dark:text-zinc-300 dark:hover:bg-white/[0.07]"
      >
        Clear Poster
      </button>
    </div>
  );
}

function ProcessingReportCard({ activeEntry }) {
  const sourceBytes = activeEntry?.processingReport?.source?.bytes;
  const optimizedBytes = activeEntry?.processingReport?.optimized?.bytes;
  const bytesSaved = activeEntry?.processingReport?.optimized?.bytesSaved;
  const compressionRatio = activeEntry?.processingReport?.optimized?.compressionRatio;

  return (
    <div className="min-w-0 rounded-2xl border border-zinc-200/70 bg-white/90 p-3.5 dark:border-zinc-800/80 dark:bg-[#111317]">
      <div className="flex items-center gap-2">
        <CheckCircle2 size={13} className="text-zinc-400 dark:text-zinc-500" />
        <div className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">
          Processing Report
        </div>
      </div>

      <div className="mt-2.5 space-y-2 text-xs text-zinc-600 dark:text-zinc-400">
        {activeEntry?.processingReport?.error && (
          <div className="rounded-lg bg-rose-50/85 px-3 py-2 text-rose-700 dark:bg-rose-950/25 dark:text-rose-200">
            {activeEntry.processingReport.error}
          </div>
        )}

        <div className="grid min-w-0 grid-cols-2 gap-1.5">
          <div className="rounded-lg bg-zinc-50/80 px-2.5 py-2 dark:bg-[#181a1e]">
            <div className="text-[10px] text-zinc-500 dark:text-zinc-400">Source</div>
            <div className="mt-0.5 text-xs font-semibold text-zinc-900 dark:text-zinc-100">
              {formatKilobytes(sourceBytes) || "n/a"}
            </div>
          </div>
          <div className="rounded-lg bg-zinc-50/80 px-2.5 py-2 dark:bg-[#181a1e]">
            <div className="text-[10px] text-zinc-500 dark:text-zinc-400">Optimized</div>
            <div className="mt-0.5 text-xs font-semibold text-zinc-900 dark:text-zinc-100">
              {formatKilobytes(optimizedBytes) || "n/a"}
            </div>
          </div>
          <div className="rounded-lg bg-zinc-50/80 px-2.5 py-2 dark:bg-[#181a1e]">
            <div className="text-[10px] text-zinc-500 dark:text-zinc-400">Saved</div>
            <div className="mt-0.5 text-xs font-semibold text-zinc-900 dark:text-zinc-100">
              {formatKilobytes(bytesSaved) || "n/a"}
            </div>
          </div>
          <div className="rounded-lg bg-zinc-50/80 px-2.5 py-2 dark:bg-[#181a1e]">
            <div className="text-[10px] text-zinc-500 dark:text-zinc-400">Ratio</div>
            <div className="mt-0.5 text-xs font-semibold text-zinc-900 dark:text-zinc-100">
              {compressionRatio != null ? `${compressionRatio}%` : "n/a"}
            </div>
          </div>
        </div>

        {activeEntry?.lastProcessedAt && (
          <div className="rounded-lg bg-zinc-50/70 px-2.5 py-2 dark:bg-[#17191d]">
            <span className="text-[10px] text-zinc-500 dark:text-zinc-400">Last processed</span>
            <div className="mt-0.5 text-[11px] font-medium text-zinc-900 dark:text-zinc-100">
              {new Date(activeEntry.lastProcessedAt).toLocaleString()}
            </div>
          </div>
        )}

        {activeEntry?.warnings?.length ? (
          <div className="rounded-lg bg-zinc-50/70 px-2.5 py-2 dark:bg-[#17191d]">
            <div className="text-[10px] font-medium text-zinc-500 dark:text-zinc-400">Warnings</div>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {activeEntry.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="rounded-lg bg-zinc-50/70 px-2.5 py-2 text-zinc-500 dark:bg-[#17191d] dark:text-zinc-400">
            No processing warnings.
          </div>
        )}
      </div>
    </div>
  );
}

export default function Step3DAssetsViewer({ state, setState, ensureDraft }) {
  const variants = useMemo(() => state?.variants || [], [state?.variants]);
  const [selectedKey, setSelectedKey] = useState("");
  const [busyLabel, setBusyLabel] = useState("");
  const [modelTransfer, setModelTransfer] = useState(null);
  const [viewerDraft, setViewerDraft] = useState(DEFAULT_VIEWER_CONFIG);
  const [appliedCameraConfig, setAppliedCameraConfig] = useState(DEFAULT_VIEWER_CONFIG);
  const [viewerInstanceKey, setViewerInstanceKey] = useState(0);
  const [modelReady, setModelReady] = useState(false);
  const [viewerError, setViewerError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [pollingError, setPollingError] = useState("");
  const viewerRef = useRef(null);
  const modelInputRef = useRef(null);
  const posterInputRef = useRef(null);
  const pendingCameraPoseRef = useRef(null);
  const cameraCommitTimeoutRef = useRef(null);

  const showError = async (title, message) => {
    await Swal.fire({
      ...businessSwalBase,
      icon: "error",
      title,
      html: String(message || "Something went wrong."),
      confirmButtonText: "OK",
    });
  };

  useEffect(() => {
    if (!feedback) return undefined;
    const timeoutId = window.setTimeout(() => setFeedback(""), 2400);
    return () => window.clearTimeout(timeoutId);
  }, [feedback]);

  useEffect(
    () => () => {
      if (cameraCommitTimeoutRef.current) {
        window.clearTimeout(cameraCommitTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    setState((prev) => {
      const nextEntries = reconcileEntries(prev?.threeD?.variants || [], prev?.variants || []);
      const currentEntries = prev?.threeD?.variants || [];
      if (JSON.stringify(nextEntries) === JSON.stringify(currentEntries)) {
        return prev;
      }
      return {
        ...prev,
        threeD: {
          ...(prev.threeD || {}),
          variants: nextEntries,
        },
      };
    });
  }, [setState, state.variants]);

  const entries = useMemo(
    () => reconcileEntries(state?.threeD?.variants || [], variants),
    [state?.threeD?.variants, variants]
  );

  useEffect(() => {
    if (!entries.some((entry) => entry.status === "PROCESSING")) {
      setPollingError("");
    }
  }, [entries]);

  useEffect(() => {
    if (!entries.length) {
      setSelectedKey("");
      return;
    }
    if (!entries.some((entry) => entry.variantKey === selectedKey)) {
      const preferred = entries.find((entry) => entry.status && entry.status !== "ARCHIVED") || entries[0];
      setSelectedKey(preferred.variantKey);
    }
  }, [entries, selectedKey]);

  const activeEntry = useMemo(
    () => entries.find((entry) => entry.variantKey === selectedKey) || entries[0] || null,
    [entries, selectedKey]
  );

  const activeVariant = useMemo(
    () => variants.find((variant) => makeVariantKey(variant.optionValues || {}) === activeEntry?.variantKey) || null,
    [variants, activeEntry]
  );
  const isBusy = Boolean(busyLabel);
  const savedViewerConfig = useMemo(
    () => mergeViewerConfig(activeEntry?.viewerConfig || DEFAULT_VIEWER_CONFIG),
    [activeEntry?.viewerConfig]
  );
  const activePreset =
    ENVIRONMENT_PRESETS[viewerDraft.environmentPreset] || ENVIRONMENT_PRESETS.neutral;
  const hasReadyModel =
    activeEntry?.status === "READY" &&
    Boolean(activeEntry?.previewUrl) &&
    Boolean(activeEntry?.optimizedAssetId);
  const canInteractViewer = hasReadyModel && modelReady && !viewerError && !isBusy;
  const hasUnsavedChanges = !viewerConfigsEqual(viewerDraft, savedViewerConfig);
  const usesSystemDefault = viewerConfigsEqual(savedViewerConfig, DEFAULT_VIEWER_CONFIG);
  const canSaveCurrentView = canInteractViewer && hasUnsavedChanges;
  const canRevertUnsaved = !isBusy && hasUnsavedChanges;
  const canSaveSystemDefault =
    hasReadyModel &&
    !isBusy &&
    (!viewerConfigsEqual(savedViewerConfig, DEFAULT_VIEWER_CONFIG) ||
      !viewerConfigsEqual(viewerDraft, DEFAULT_VIEWER_CONFIG));

  // Intentionally keyed to variant/model identity rather than every viewerConfig echo
  // from the server, so saving the current view does not remount and visually reset
  // the live camera framing the user is already looking at.
  useEffect(() => {
    const nextConfig = mergeViewerConfig(activeEntry?.viewerConfig || DEFAULT_VIEWER_CONFIG);
    setViewerDraft(nextConfig);
    setAppliedCameraConfig(nextConfig);
    setViewerInstanceKey((value) => value + 1);
    setModelReady(false);
    setViewerError("");
    pendingCameraPoseRef.current = null;
    if (cameraCommitTimeoutRef.current) {
      window.clearTimeout(cameraCommitTimeoutRef.current);
      cameraCommitTimeoutRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeEntry?.variantKey,
    activeEntry?.modelAssetId,
    activeEntry?.optimizedAssetId,
    activeEntry?.status,
  ]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    viewer.environmentImage = activePreset.environmentImage;
    viewer.toneMapping = activePreset.toneMapping;
    viewer.exposure = Number(viewerDraft.exposure || 1);
    viewer.autoRotate = Boolean(viewerDraft.autoRotate);
  }, [
    activePreset.environmentImage,
    activePreset.toneMapping,
    viewerDraft.autoRotate,
    viewerDraft.exposure,
    viewerInstanceKey,
    activeEntry?.variantKey,
  ]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !hasReadyModel) return undefined;

    const handleLoad = () => {
      setModelReady(true);
      setViewerError("");
    };

    const handleError = () => {
      setModelReady(false);
      setViewerError("The 3D preview could not be rendered.");
    };

    const handleCameraChange = (event) => {
      if (event?.detail?.source !== "user-interaction") return;

      pendingCameraPoseRef.current = captureCameraPoseFromElement(viewer);

      if (cameraCommitTimeoutRef.current) {
        window.clearTimeout(cameraCommitTimeoutRef.current);
      }

      cameraCommitTimeoutRef.current = window.setTimeout(() => {
        const pendingPose = pendingCameraPoseRef.current;
        cameraCommitTimeoutRef.current = null;
        if (!pendingPose) return;

        setViewerDraft((prev) => {
          const next = mergeViewerConfig({
            ...prev,
            ...pendingPose,
          });
          return viewerConfigsEqual(prev, next) ? prev : next;
        });
      }, 120);
    };

    viewer.addEventListener("load", handleLoad);
    viewer.addEventListener("error", handleError);
    viewer.addEventListener("camera-change", handleCameraChange);

    return () => {
      viewer.removeEventListener("load", handleLoad);
      viewer.removeEventListener("error", handleError);
      viewer.removeEventListener("camera-change", handleCameraChange);
    };
  }, [hasReadyModel, viewerInstanceKey, activeEntry?.variantKey]);

  const applyServerEntry = (serverEntry) => {
    if (!serverEntry?.variantKey) return;
    setState((prev) => {
      const nextEntries = reconcileEntries(prev?.threeD?.variants || [], prev?.variants || []).map((entry) =>
        entry.variantKey === serverEntry.variantKey
          ? normalizeEntry(serverEntry, prev.variants.find((variant) => makeVariantKey(variant.optionValues || {}) === serverEntry.variantKey) || {})
          : entry
      );
      return {
        ...prev,
        threeD: {
          ...(prev.threeD || {}),
          variants: nextEntries,
        },
      };
    });
  };

  const applyServerList = (serverList = []) => {
    setState((prev) => {
      const prevVariants = prev?.variants || [];
      const updated = reconcileEntries(prev?.threeD?.variants || [], prevVariants).map((entry) => {
        const match = serverList.find((serverEntry) => (serverEntry.variantKey || "") === entry.variantKey);
        if (!match) return entry;
        const variant =
          prevVariants.find((currentVariant) => makeVariantKey(currentVariant.optionValues || {}) === entry.variantKey) || {};
        return normalizeEntry(match, variant);
      });
      if (JSON.stringify(updated) === JSON.stringify(prev?.threeD?.variants || [])) return prev;
      return {
        ...prev,
        threeD: {
          ...(prev.threeD || {}),
          variants: updated,
        },
      };
    });
  };

  const describePollingError = (error) => {
    const status = Number(error?.response?.status || 0);
    if (status === 404) {
      return "The dedicated 3D status refresh route is unavailable in this deployment. Refresh the product or redeploy the backend 3D route before trusting the processing state.";
    }
    if (status >= 500) {
      return "The backend failed while refreshing 3D processing status. Check the deployed product and 3D service logs before assuming the model is still processing.";
    }
    return error?.response?.data?.message ||
      error?.message ||
      "Unable to refresh 3D processing status right now.";
  };

  const readServerList = async (productId) => {
    try {
      const res = await businessProductsAPI.listVariant3DAssets(productId);
      return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
    } catch (primaryError) {
      const productResponse = await businessProductsAPI.getById(productId);
      const product = productResponse?.data || productResponse || {};
      const fallbackList = Array.isArray(product?.variant3d) ? product.variant3d : [];
      if (!fallbackList.length) {
        throw primaryError;
      }
      setPollingError(describePollingError(primaryError));
      return fallbackList;
    }
  };

  // Auto-poll when any variant is PROCESSING so the UI updates when optimization finishes
  const pollingRef = useRef(null);
  useEffect(() => {
    const productId = state?.draftId;
    const hasProcessing = entries.some((e) => e.status === "PROCESSING");
    if (!productId || !hasProcessing) {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      return undefined;
    }
    pollingRef.current = setInterval(async () => {
      try {
        const serverList = await readServerList(productId);
        if (!serverList.length) return;
        applyServerList(serverList);
        if (!serverList.some((entry) => entry.status === "PROCESSING")) {
          setPollingError("");
        }
      } catch (error) {
        setPollingError(describePollingError(error));
      }
    }, 5000);
    return () => { clearInterval(pollingRef.current); pollingRef.current = null; };
    // Intentionally keyed to the active product and current 3D entries.
    // The helper functions close over the latest state and do not need to
    // force a fresh polling interval on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.draftId, entries, setState]);

  const ensureProductId = async () => {
    if (state?.draftId) return state.draftId;
    if (typeof ensureDraft !== "function") {
      throw new Error("A draft product is required before managing variant 3D assets.");
    }
    const draftId = await ensureDraft();
    if (!draftId) {
      throw new Error("The draft product could not be created.");
    }
    return draftId;
  };

  const persistActiveEntry = async (patch = {}) => {
    if (!activeEntry || !activeVariant) {
      throw new Error("Select a variant first.");
    }

    const productId = await ensureProductId();
    const payload = {
      variantId: activeVariant.id || null,
      variantKey: activeEntry.variantKey,
      modelAssetId: patch.modelAssetId ?? activeEntry.modelAssetId ?? null,
      posterAssetId: patch.posterAssetId ?? activeEntry.posterAssetId ?? null,
      posterUrl: patch.posterUrl ?? null,
      clearPoster: Boolean(patch.clearPoster),
    };

    if (Object.prototype.hasOwnProperty.call(patch, "viewerConfig")) {
      payload.viewerConfig = patch.viewerConfig;
    }
    if (patch.saveViewerConfig) {
      payload.saveViewerConfig = true;
    }

    const response = await businessProductsAPI.upsertVariant3D(productId, payload);
    applyServerEntry(response?.data || response);
  };

  const handleModelUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !activeEntry || !activeVariant) return;

    try {
      const action = activeEntry.modelAssetId ? "Replacing" : "Uploading";
      setModelTransfer({ action, fileName: file.name, phase: "uploading" });
      setBusyLabel("Uploading model");
      const productId = await ensureProductId();
      const upload = await businessProductsAPI.uploadVariant3DModel(file, {
        productId,
        variantId: activeVariant.id || null,
        variantKey: activeEntry.variantKey,
      });

      setBusyLabel("Saving model");
      setModelTransfer((current) => current ? { ...current, phase: "saving" } : current);
      await persistActiveEntry({
        modelAssetId: upload?.assetId || upload?.data?.assetId || null,
      });
      setFeedback("GLB uploaded. Optimization is running in the background.");
    } catch (error) {
      await showError(
        "3D Upload Failed",
        formatUploadError(error?.response?.data?.message || error?.message)
      );
    } finally {
      setModelTransfer(null);
      setBusyLabel("");
    }
  };

  const handlePosterUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !activeEntry || !activeVariant) return;

    try {
      setBusyLabel("Uploading poster");
      const productId = await ensureProductId();
      const upload = await businessProductsAPI.uploadVariant3DPoster(file, {
        productId,
        variantId: activeVariant.id || null,
        variantKey: activeEntry.variantKey,
      });
      await persistActiveEntry({
        posterAssetId: upload?.assetId || upload?.data?.assetId || null,
      });
      setFeedback("Poster updated for the selected variant.");
    } catch (error) {
      await showError("Poster Upload Failed", error?.response?.data?.message || error?.message);
    } finally {
      setBusyLabel("");
    }
  };

  const remountViewerWithConfig = (config) => {
    const nextConfig = mergeViewerConfig(config);
    setViewerDraft(nextConfig);
    setAppliedCameraConfig(nextConfig);
    setViewerInstanceKey((value) => value + 1);
    setModelReady(false);
    setViewerError("");
  };

  const handleSaveCurrentView = async (options = {}) => {
    if (!hasReadyModel) {
      if (!options.silent) {
        await showError("No 3D model", "Upload and process a GLB first before saving viewer settings.");
      }
      return false;
    }

    const nextConfig = captureViewerConfigFromElement(viewerRef.current, viewerDraft);
    setViewerDraft(nextConfig);

    try {
      setBusyLabel("Saving viewer settings");
      await persistActiveEntry({ viewerConfig: nextConfig, saveViewerConfig: true });
      setFeedback("Viewer settings saved.");
      return true;
    } catch (error) {
      if (!options.silent) {
        await showError("Save Failed", error?.response?.data?.message || error?.message);
      }
      return false;
    } finally {
      setBusyLabel("");
    }
  };

  const handleRevertToSavedView = () => {
    remountViewerWithConfig(savedViewerConfig);
    setFeedback("Unsaved viewer changes reverted.");
  };

  const handleSaveSystemDefault = async () => {
    if (!hasReadyModel) {
      await showError("Model Not Ready", "System defaults can only be saved after the optimized model is ready.");
      return;
    }
    remountViewerWithConfig(DEFAULT_VIEWER_CONFIG);
    try {
      setBusyLabel("Saving system defaults");
      await persistActiveEntry({ viewerConfig: DEFAULT_VIEWER_CONFIG, saveViewerConfig: true });
      setFeedback("System default view saved for this variant.");
    } catch (error) {
      await showError("Reset Failed", error?.response?.data?.message || error?.message);
    } finally {
      setBusyLabel("");
    }
  };

  const handleRemoveModel = async () => {
    if (!activeEntry) return;

    try {
      setBusyLabel("Removing model");
      const productId = await ensureProductId();
      const response = await businessProductsAPI.removeVariant3D(productId, {
        variantId: activeVariant?.id || null,
        variantKey: activeEntry.variantKey,
      });
      applyServerEntry(response?.data || response);
      setFeedback("The model and linked preview assets were archived for this variant.");
    } catch (error) {
      await showError("Remove Failed", error?.response?.data?.message || error?.message);
    } finally {
      setBusyLabel("");
    }
  };

  const handleRetryProcessing = async () => {
    if (!activeEntry?.modelAssetId) return;
    try {
      setBusyLabel("Retrying optimization");
      await persistActiveEntry({ modelAssetId: activeEntry.modelAssetId });
      setFeedback("Optimization retry started.");
    } catch (error) {
      await showError("Retry Failed", error?.response?.data?.message || error?.message);
    } finally {
      setBusyLabel("");
    }
  };

  const handleUsePosterUrl = async (url) => {
    if (!url) return;
    try {
      setBusyLabel("Assigning poster");
      await persistActiveEntry({ posterUrl: url });
      setFeedback("Poster assigned from an existing product image.");
    } catch (error) {
      await showError("Poster Assign Failed", error?.response?.data?.message || error?.message);
    } finally {
      setBusyLabel("");
    }
  };

  const handleClearPoster = async () => {
    try {
      setBusyLabel("Clearing poster");
      await persistActiveEntry({ clearPoster: true });
      setFeedback("Poster cleared.");
    } catch (error) {
      await showError("Poster Clear Failed", error?.response?.data?.message || error?.message);
    } finally {
      setBusyLabel("");
    }
  };

  const handleSelectVariant = async (nextKey) => {
    if (!nextKey || nextKey === activeEntry?.variantKey || isBusy) return;

    if (hasUnsavedChanges && hasReadyModel) {
      const result = await Swal.fire({
        ...businessSwalBase,
        icon: "warning",
        title: "Unsaved 3D view changes",
        html:
          "This variant has unsaved camera or viewer changes. Save them before switching variants, or discard them.",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Save and Switch",
        denyButtonText: "Discard Changes",
        cancelButtonText: "Stay Here",
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        const saved = await handleSaveCurrentView({ silent: true });
        if (!saved) return;
      } else if (!result.isDenied) {
        return;
      }
    }

    setSelectedKey(nextKey);
  };

  const coverImage = state?.media?.productImages?.[0] || null;
  const variantImage = activeVariant?.imageUrl || null;

  if (!variants.length) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-5 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-400">
        Create variant combinations first. The 3D workflow is variant-based and only becomes available after the variant matrix exists.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <VariantRail
        entries={entries}
        activeVariantKey={activeEntry?.variantKey}
        onSelectVariant={handleSelectVariant}
      />

      {/* Workspace header */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-zinc-200 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-[#121214]">
        <div className="flex flex-wrap items-center gap-2.5 text-sm">
          <span className="font-semibold text-zinc-900 dark:text-zinc-100">
            {activeEntry?.label || "Variant"}
          </span>
          <span className="rounded-md border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
            {activeEntry?.variantKey || "—"}
          </span>
          <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusChip(activeEntry?.status)}`}>
            {statusLabel(activeEntry?.status)}
          </span>
          {busyLabel && (
            <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-200">
              {busyLabel}
            </span>
          )}
        </div>
        {feedback && (
          <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200">
            {feedback}
          </span>
        )}
      </div>

      {pollingError && entries.some((entry) => entry.status === "PROCESSING") && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          {pollingError}
        </div>
      )}

      {/* Main 2-column workspace: viewer (primary) + sidebar (secondary) */}
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_300px]">
        {/* Left — Viewer (primary) */}
        <div className="flex min-w-0 flex-col gap-3">
          <ViewerToolbar
            activeEntry={activeEntry}
            isBusy={isBusy}
            modelInputRef={modelInputRef}
            posterInputRef={posterInputRef}
            onModelUpload={handleModelUpload}
            onPosterUpload={handlePosterUpload}
            onRemoveModel={handleRemoveModel}
          />

          <ViewerStage
            activeEntry={activeEntry}
            activePreset={activePreset}
            hasReadyModel={hasReadyModel}
            modelReady={modelReady}
            viewerError={viewerError}
            viewerInstanceKey={viewerInstanceKey}
            viewerRef={viewerRef}
            appliedCameraConfig={appliedCameraConfig}
            viewerDraft={viewerDraft}
            modelTransfer={modelTransfer}
          />
        </div>

        {/* Right — Settings sidebar (secondary) */}
        <div className="min-w-0 space-y-3">
          <ViewerSettingsCard
            activePreset={activePreset}
            viewerDraft={viewerDraft}
            canInteractViewer={canInteractViewer}
            hasUnsavedChanges={hasUnsavedChanges}
            usesSystemDefault={usesSystemDefault}
            canSaveCurrentView={canSaveCurrentView}
            canRevertUnsaved={canRevertUnsaved}
            canSaveSystemDefault={canSaveSystemDefault}
            activeEntry={activeEntry}
            isBusy={isBusy}
            onEnvironmentChange={(value) =>
              setViewerDraft((prev) => ({ ...prev, environmentPreset: value }))
            }
            onExposureChange={(e) =>
              setViewerDraft((prev) => ({ ...prev, exposure: e.target.value }))
            }
            onAutoRotateChange={(e) =>
              setViewerDraft((prev) => ({ ...prev, autoRotate: e.target.checked }))
            }
            onSaveCurrentView={handleSaveCurrentView}
            onRevertToSavedView={handleRevertToSavedView}
            onSaveSystemDefault={handleSaveSystemDefault}
            onRetryProcessing={handleRetryProcessing}
          />

          <PosterCard
            activeEntry={activeEntry}
            variantImage={variantImage}
            coverImage={coverImage}
            isBusy={isBusy}
            onUsePosterUrl={handleUsePosterUrl}
            onClearPoster={handleClearPoster}
          />

          <ProcessingReportCard activeEntry={activeEntry} />
        </div>
      </div>
    </div>
  );
}
